import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Context } from '@xxtract/tabelfilters';

import { EmptyState } from '@/components/feedback/EmptyState';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { Filterbalk, SorteerKeuze, useServerTabelData, useStartadres, useTabelStand, type ServerAntwoord } from '@/components/tabel';
import { useLijstStandStore, type Weergave } from '@/stores/lijstStandStore';
import { useClientId } from '@/features/value/hooks/useClientId';
import { useEffectiveAccess } from '@/lib/effectiveAccess';
import { useMediaLibrary } from '../hooks/useMediaLibrary';
import {
  ZipLimitExceededError,
  downloadLibraryZip,
  downloadProductMediaItem,
  fetchLibraryWebImage,
  triggerBlobDownload,
  type ConvertFormat,
  type ConvertSize,
  type MediaLibraryItem,
} from '../media.api';
import { MEDIA_LIJST, MEDIA_PAGINAFILTER, mediaPaginaSleutel, mediaSleutel } from '../mediaLijst';
import { MediaFilters } from '../components/MediaFilters';
import { MediaGrid } from '../components/MediaGrid';
import { MediaList } from '../components/MediaList';
import { MediaBulkBar } from '../components/MediaBulkBar';

/**
 * Hoeveel bestanden er op een pagina passen. De server begrenst dit op 100 en geeft een fout
 * daarbuiten, dus het scherm toetst de keuze zelf: een geknutseld webadres mag de bibliotheek
 * niet breken.
 */
const PAGE_SIZE_CHOICES = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;
type MediaLibraryView = 'grid' | 'list';
const FILTER_TEST_ID = 'media';
const NAAR_WEERGAVE: Record<MediaLibraryView, Weergave> = { grid: 'raster', list: 'lijst' };

function isPageSize(value: number | undefined): value is (typeof PAGE_SIZE_CHOICES)[number] {
  return (PAGE_SIZE_CHOICES as readonly (number | undefined)[]).includes(value);
}

/** De ene waarde van een enkelvoudig paginafilter (tekst of keuzelijst met één keuze). */
function enkel(waarde: unknown): string | undefined {
  if (typeof waarde === 'string') return waarde === '' ? undefined : waarde;
  return Array.isArray(waarde) && typeof waarde[0] === 'string' ? waarde[0] : undefined;
}

/**
 * DAM library page (h11-1, FR49/FR56, mockup dam.html): one searchable, filterable grid of
 * every known media file across the tenant's products, aggregated read-time from the
 * mediaserver via the h2-1 BFF module — no media storage in the portal.
 */
export function MediaLibraryPage() {
  const { t } = useTranslation();
  const gln = useClientId();
  const { role } = useEffectiveAccess();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [zipLimitExceeded, setZipLimitExceeded] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [convertFormat, setConvertFormat] = useState<ConvertFormat | null>(null);
  const [convertSize, setConvertSize] = useState<ConvertSize>('original');

  const kopRef = useRef<HTMLHeadingElement>(null);

  // Lijststand per klant (spec-tabelfilters story 10, CAP-14): kolomfilters, sortering, pagina, rijen
  // per pagina en weergave in `media:<gln>`; zoeken, merk en "niet gekoppeld" als paginafilter in
  // `media.pagina:<gln>`. Een oude link werkt één keer als startfilter en verdwijnt uit het adres.
  const lijstSleutel = mediaSleutel(gln);
  const paginaSleutel = mediaPaginaSleutel(gln);
  const startadres = useMemo(
    () => [
      { lijst: MEDIA_PAGINAFILTER, standSleutel: paginaSleutel },
      { lijst: MEDIA_LIJST, standSleutel: lijstSleutel },
    ],
    [paginaSleutel, lijstSleutel],
  );
  const { start, geweigerd, wisGeweigerd } = useStartadres(startadres);
  const stand = useTabelStand(MEDIA_LIJST, lijstSleutel, { start: start[lijstSleutel], afhankelijkVan: paginaSleutel, paginagrootte: DEFAULT_PAGE_SIZE });
  const bewaardePaginafilters = useLijstStandStore((s) => s.standen[paginaSleutel]?.filters);
  // Een link met alleen lijstparameters (bijv. `?gtin=`) opent precies dat filter: een eerder
  // bewaard zoekwoord, merk of "niet gekoppeld" geldt dan niet (§7).
  const alleenLijstLink = start[lijstSleutel] !== undefined && start[paginaSleutel] === undefined;
  const paginafilters = start[paginaSleutel]?.filters ?? (alleenLijstLink ? {} : bewaardePaginafilters) ?? {};
  useLayoutEffect(() => {
    // Gewone `zet`, niet `zetPaginafilter`: die zou de pagina uit de link op 1 zetten.
    if (alleenLijstLink) useLijstStandStore.getState().zet(paginaSleutel, { filters: {} });
  }, [alleenLijstLink, paginaSleutel]);
  const query = enkel(paginafilters.zoek) ?? '';
  const brandId = enkel(paginafilters.merk) ?? null;
  const gekoppeld = enkel(paginafilters.gekoppeld);
  const linked = gekoppeld === 'false' ? false : gekoppeld === 'true' ? true : undefined;
  const startWeergave = enkel(start[paginaSleutel]?.filters.weergave) as MediaLibraryView | undefined;
  const view: MediaLibraryView = startWeergave ?? (stand.weergave === 'lijst' ? 'list' : 'grid');
  // Een weergave uit de link hoort daarna bij de lijststand, zodat hij terugkomen overleeft.
  const zetWeergave = stand.zetWeergave;
  useLayoutEffect(() => {
    if (startWeergave) zetWeergave(NAAR_WEERGAVE[startWeergave]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij een nieuwe link
  }, [startWeergave]);
  const pageSize = isPageSize(stand.paginagrootte) ? stand.paginagrootte : DEFAULT_PAGE_SIZE;

  const [antwoord, setAntwoord] = useState<ServerAntwoord>();
  const tabel = useServerTabelData(MEDIA_LIJST, { ...stand, paginagrootte: pageSize }, antwoord);
  const { page: paginaParameter, pageSize: _grootte, ...kolommen } = tabel.parameters;
  const context = useMemo<Context>(() => ({ t }), [t]);

  // Welke bestanden op dit moment worden opgehaald. Eén vlag voor alles zou elke tegel bezig
  // laten lijken; hier weet iedere tegel of het om hém gaat.
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // Een medewerker zonder gekozen klant: de bibliotheek hoort bij één klant, dus zonder klant
  // valt er niets te tonen. Een klant heeft geen klantkiezer; levert zijn sessie geen nummer
  // (de bekende terugval bij een onvolledig gebruikersprofiel), dan blijft het oude gedrag
  // staan — dan leidt de server de klant uit de sessie af, precies zoals voorheen.
  const geenKlantGekozen = gln === '' && role !== 'customer';

  const { items, meta, data: antwoordData, isLoading, isError, error, refetch } = useMediaLibrary({
    ...(query === '' ? {} : { query }),
    ...(brandId == null ? {} : { brandId }),
    ...(linked == null ? {} : { linked }),
    // Een medewerker die geen klant heeft gekozen heeft hier een lege waarde; die sturen we
    // niet mee, zodat het scherm zelf uitlegt dat er eerst een klant gekozen moet worden.
    ...(gln === '' ? {} : { gln }),
    kolommen,
    page: Number(paginaParameter),
    pageSize,
  }, !geenKlantGekozen);

  // Facetten, suggesties en totaal uit een écht antwoord terug naar de filterbalk. Nooit uit de
  // lege terugval tijdens het laden: totaal 0 zou de bewaarde pagina even op 1 zetten.
  const echteMeta = antwoordData?.meta;
  useEffect(() => {
    if (echteMeta) setAntwoord({ totaal: echteMeta.total, facetten: echteMeta.facetten, suggesties: echteMeta.suggesties, afgekapt: echteMeta.afgekapt });
  }, [echteMeta]);

  useEffect(() => {
    const knownIds = new Set(items.map((item) => item.id));
    setSelectedIds((previous) => {
      const next = new Set([...previous].filter((id) => knownIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
    // Ook de downloadtoestand: wisselt de gebruiker van klant of blaadert hij door, dan zou een
    // knop anders eeuwig blijven draaien voor een bestand dat niet meer in beeld is.
    setDownloadingIds((previous) => {
      const next = new Set([...previous].filter((id) => knownIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [items]);

  function zetPaginafilter(sleutel: string, waarde: string | null) {
    const { [sleutel]: _oud, weergave: _weergave, ...rest } = paginafilters;
    // Een wijziging zet de lijst op pagina 1 (afhankelijkVan), ook bij een merkwissel.
    useLijstStandStore.getState().zetPaginafilter(paginaSleutel, waarde == null || waarde === '' ? rest : { ...rest, [sleutel]: sleutel === 'zoek' || sleutel === 'merk' ? waarde : [waarde] });
  }

  function handleQueryChange(value: string) { zetPaginafilter('zoek', value); }
  function handleBrandChange(value: string | null) { zetPaginafilter('merk', value); }
  function handleLinkedChange(value: boolean | null) { zetPaginafilter('gekoppeld', value == null ? null : String(value)); }
  function handlePageSizeChange(value: number) { stand.zetPaginagrootte(value); }
  // Wisselen van weergave laat de pagina staan: anders zie je ineens andere bestanden.
  function handleViewChange(value: MediaLibraryView) { stand.zetWeergave(NAAR_WEERGAVE[value]); }
  function handlePageChange(nextPage: number) { stand.zetPagina(nextPage); }

  function productDetailHref(item: MediaLibraryItem): string | undefined {
    return item.productId == null ? undefined : `/products/${item.productId}`;
  }

  function toggleSelect(id: string) {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Het adres van de grote versie, pas opgevraagd wanneer iemand een bestand aanklikt. */
  async function loadWebImage(item: MediaLibraryItem) {
    return fetchLibraryWebImage(item.id, gln === '' ? undefined : gln);
  }

  async function handleDownload(item: MediaLibraryItem) {
    if (downloadingIds.has(item.id)) return;
    setDownloadFailed(false);
    setDownloadingIds((previous) => new Set(previous).add(item.id));
    try {
      await downloadProductMediaItem({ gln, gtin: item.gtin, targetMarket: item.targetMarket }, item.id, item.fileName);
    } catch {
      setDownloadFailed(true);
    } finally {
      // In een `finally`, niet na de `await`: een mislukte download hoort de knop net zo goed
      // vrij te geven. Een knop die blijft draaien is erger dan geen indicator.
      setDownloadingIds((previous) => {
        const next = new Set(previous);
        next.delete(item.id);
        return next;
      });
    }
  }

  async function handleDownloadZip() {
    if (isDownloadingZip) return;
    setZipLimitExceeded(false);
    setDownloadFailed(false);
    setIsDownloadingZip(true);
    try {
      const blob = await downloadLibraryZip(
        [...selectedIds],
        convertFormat == null ? undefined : { format: convertFormat, size: convertSize },
        gln === '' ? undefined : gln,
      );
      triggerBlobDownload(blob, 'mediabibliotheek.zip');
    } catch (error) {
      if (error instanceof ZipLimitExceededError) setZipLimitExceeded(true);
      else setDownloadFailed(true);
    } finally {
      setIsDownloadingZip(false);
    }
  }

  const degraded = meta.degraded;
  const showEmptyState = !geenKlantGekozen && !isLoading && !isError && items.length === 0 && meta.total === 0;
  const selectedItems = items.filter((item) => selectedIds.has(item.id));
  const selectedSizeBytes = selectedItems.reduce((sum, item) => sum + (item.sizeBytes ?? 0), 0);
  // Documents (SDS) are never rasterized (FR51) — this precomputed count powers the
  // "N documents included unchanged" notice without any extra server round trip.
  const selectedDocumentCount = selectedItems.filter((item) => item.displayType === 'sds').length;

  return (
    <div className="space-y-5" data-testid="media-library-page">
      <div>
        <h1 className="text-[1.25rem] font-bold tracking-[-0.02em] text-[var(--foreground-heading)] outline-none" ref={kopRef} tabIndex={-1}>
          {t('media.library.title')}
        </h1>
        {!geenKlantGekozen && (
          <p className="mt-[3px] text-[0.8125rem] text-[var(--foreground-muted)]">
            {t('media.library.subtitle', { count: meta.total })}
          </p>
        )}
      </div>

      <div className="flex items-start gap-2.5 rounded-[10px] border border-[var(--border)] bg-[var(--background-muted)] p-3 px-4">
        <span aria-hidden="true" className="material-symbols-outlined shrink-0 text-[16px] text-[var(--secondary)]">lightbulb</span>
        <p className="text-xs text-[var(--foreground)]">{t('media.library.tip')}</p>
      </div>

      {geenKlantGekozen && (
        <div
          className="flex items-start gap-2.5 rounded-[10px] border border-[var(--border)] bg-[var(--background-muted)] p-3 px-4"
          data-testid="media-library-no-client"
        >
          <span aria-hidden="true" className="material-symbols-outlined shrink-0 text-[16px] text-[var(--secondary)]">info</span>
          <p className="text-xs text-[var(--foreground)]">{t('media.library.noClient')}</p>
        </div>
      )}

      {!geenKlantGekozen && isError && <ApiErrorMessage error={error} onRetry={() => void refetch()} />}

      {!geenKlantGekozen && !isError && (
        <>
          <FilterRejectedMessage
            baseTestId={FILTER_TEST_ID}
            focusNaWissen={kopRef}
            linkGeweigerd={geweigerd}
            onClear={() => {
              stand.reset();
              wisGeweigerd();
            }}
          />
          <Filterbalk
            context={context}
            data={tabel}
            lijst={MEDIA_LIJST}
            paginafilters={
              <MediaFilters
                brandId={brandId}
                brands={meta.brands}
                linked={linked ?? null}
                onBrandChange={handleBrandChange}
                onLinkedChange={handleLinkedChange}
                onPageSizeChange={handlePageSizeChange}
                onQueryChange={handleQueryChange}
                onViewChange={handleViewChange}
                pageSize={pageSize}
                pageSizeChoices={PAGE_SIZE_CHOICES}
                query={query}
                showViewControls={!degraded}
                view={view}
              />
            }
            stand={stand}
          />
        </>
      )}

      {isLoading && !isError && (
        <p className="text-sm text-[var(--foreground-muted)]">{t('common.loading')}</p>
      )}

      {degraded && !isLoading && (
        <EmptyState icon="cloud_off" title={t('media.library.degraded')} variant="neutral" />
      )}

      {downloadFailed && (
        <div className="rounded-[10px] border border-[rgba(214,69,69,0.25)] bg-[var(--destructive-light)] px-4 py-3 text-sm text-[var(--foreground)]" role="alert">
          {t('errors.generic')}
        </div>
      )}

      {zipLimitExceeded && (
        <div className="flex items-start gap-2.5 rounded-[10px] border border-[var(--border)] bg-[var(--background-muted)] p-3 px-4 text-sm text-[var(--foreground)]">
          <span aria-hidden="true" className="material-symbols-outlined shrink-0 text-[16px] text-[var(--secondary)]">info</span>
          {t('media.library.bulkBar.zipLimitExceeded')}
        </div>
      )}

      {showEmptyState && !degraded && (
        <EmptyState
          description={t('media.library.empty.description')}
          icon="perm_media"
          title={t('media.library.empty.title')}
        />
      )}

      {!isLoading && !isError && !degraded && items.length > 0 && (
        <MediaGrid
          downloadingIds={downloadingIds}
          items={view === 'list' ? [] : items}
          loadWebImage={loadWebImage}
          onDownload={(item) => void handleDownload(item)}
          onPageChange={handlePageChange}
          onToggleSelect={toggleSelect}
          page={tabel.pagina}
          pageSize={pageSize}
          productHref={productDetailHref}
          renderItems={view === 'grid'}
          selectedIds={selectedIds}
          total={meta.total}
        >
          {view === 'list' ? (
            <MediaList
              downloadingIds={downloadingIds}
              items={items}
              loadWebImage={loadWebImage}
              onDownload={(item) => void handleDownload(item)}
              onSorteer={stand.klikOpKop}
              onToggleSelect={toggleSelect}
              productHref={productDetailHref}
              selectedIds={selectedIds}
              sortering={stand.sortering}
            />
          ) : (
            // Het raster heeft geen kolomkoppen: sorteren via de sorteerkeuze (op een smal scherm
            // zit die al in de filterbalk).
            <div className="hidden max-w-md md:block" data-testid="media-sorteerkeuze">
              <SorteerKeuze context={context} lijst={MEDIA_LIJST} onChange={stand.zetSortering} sortering={stand.sortering} />
            </div>
          )}
        </MediaGrid>
      )}

      <MediaBulkBar
        convertFormat={convertFormat}
        convertSize={convertSize}
        documentCount={selectedDocumentCount}
        isDownloading={isDownloadingZip}
        onConvertFormatChange={setConvertFormat}
        onConvertSizeChange={setConvertSize}
        onDeselect={() => setSelectedIds(new Set())}
        onDownloadZip={() => void handleDownloadZip()}
        selectedCount={selectedIds.size}
        totalSizeBytes={selectedSizeBytes}
      />
    </div>
  );
}
